/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ModalShell } from '../../../shared/modals/ModalShell';
 '../../../shared/ui/public-api';
import { bindNumericOnChange, numericTextInputDefaults } from '../../../shared/numeric/localeNumeric';
import {
  fetchReplacementProducts,
  type ReplacementProduct,
} from '../../infra/ordersRepo';
import type { CustomerOrderItemRow } from '../../domain/orderTypes';
import { formatQty } from '@shared/quantity';
import { formatMoney } from '../../../shared/format/public-api';

type ChangeAction = 'replace' | 'reduce_quantity' | 'remove';

export type InformMissingProposal = {
  changeType: ChangeAction;
  proposedProductId?: string;
  proposedQuantity?: number;
};

type Props = {
  visible: boolean;
  item: CustomerOrderItemRow | null;
  branchId: string;
  organizationId: string;
  sending?: boolean;
  onClose: () => void;
  onSubmit: (proposal: InformMissingProposal) => void;
};

const ACTION_OPTIONS: { key: ChangeAction; label: string; hint: string }[] = [
  { key: 'replace', label: 'Reemplazar', hint: 'Otro producto en su lugar' },
  { key: 'reduce_quantity', label: 'Bajar unidades', hint: 'Menos cantidad del mismo' },
  { key: 'remove', label: 'Eliminar', hint: 'Sacar del pedido' },
];

export function InformMissingItemModal({
  visible,
  item,
  branchId,
  organizationId,
  sending = false,
  onClose,
  onSubmit,
}: Props) {
  const [action, setAction] = useState<ChangeAction | null>(null);
  const [replacementProducts, setReplacementProducts] = useState<ReplacementProduct[]>([]);
  const [replacementLoading, setReplacementLoading] = useState(false);
  const [replacementSearch, setReplacementSearch] = useState('');
  const [selectedReplacement, setSelectedReplacement] = useState<ReplacementProduct | null>(null);
  const [replaceQty, setReplaceQty] = useState('');
  const [reduceQty, setReduceQty] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productIdRef = useRef<string | null>(null);

  const loadReplacements = useCallback(
    async (excludeProductId: string, search?: string) => {
      setReplacementLoading(true);
      try {
        const products = await fetchReplacementProducts(
          branchId,
          organizationId,
          excludeProductId,
          search,
        );
        setReplacementProducts(products);
      } catch (err) {
        presentUnexpectedFailure({ module: 'orders', op: 'error', error: err, title: 'Error', userMessage: 'No se pudieron cargar reemplazos' });
      } finally {
        setReplacementLoading(false);
      }
    },
    [branchId, organizationId],
  );

  useEffect(() => {
    if (!visible || !item) return;
    productIdRef.current = item.product_id;
    setAction(null);
    setReplacementProducts([]);
    setReplacementSearch('');
    setSelectedReplacement(null);
    setReplaceQty('');
    setReduceQty('');
  }, [visible, item]);

  useEffect(() => {
    if (!visible || !item || action !== 'replace') return;
    void loadReplacements(item.product_id);
  }, [visible, item, action, loadReplacements]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, []);

  const handleSearchChange = useCallback(
    (text: string) => {
      setReplacementSearch(text);
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      const excludeId = productIdRef.current;
      if (!excludeId) return;
      searchTimerRef.current = setTimeout(() => {
        void loadReplacements(excludeId, text);
      }, 300);
    },
    [loadReplacements],
  );

  const maxReduce = (item?.quantity ?? 1) - 1;
  const canReduce = maxReduce >= 1;
  const originalLineTotal = item ? item.line_total : 0;
  const qtyInputDefaults = numericTextInputDefaults('integer');
  const onReplaceQtyChange = bindNumericOnChange(setReplaceQty, 'integer');
  const onReduceQtyChange = bindNumericOnChange(setReduceQty, 'integer');

  const replaceQtyNum = replaceQty === '' ? NaN : Number(replaceQty);
  const reduceQtyNum = reduceQty === '' ? NaN : Number(reduceQty);
  const replaceQtyValid =
    Number.isInteger(replaceQtyNum) && replaceQtyNum >= 1;
  const reduceQtyValid =
    Number.isInteger(reduceQtyNum) && reduceQtyNum >= 1 && reduceQtyNum <= maxReduce;

  const canSubmit = (() => {
    if (!item || !action || sending) return false;
    if (action === 'remove') return true;
    if (action === 'reduce_quantity') return reduceQtyValid;
    if (action === 'replace') {
      if (!selectedReplacement || !replaceQtyValid) return false;
      if (replaceQtyNum > selectedReplacement.stock) return false;
      return true;
    }
    return false;
  })();

  const preview = (() => {
    if (!item || !action) return null;
    if (action === 'remove') {
      return {
        summary: `Eliminar ${item.product_name} (${formatQty(item.quantity, item.unit_of_measure)})`,
        delta: -originalLineTotal,
      };
    }
    if (action === 'reduce_quantity' && reduceQtyValid) {
      const newTotal = item.unit_price * reduceQtyNum;
      return {
        summary: `${item.product_name}: ${formatQty(item.quantity, item.unit_of_measure)} → ${formatQty(reduceQtyNum, item.unit_of_measure)}`,
        delta: newTotal - originalLineTotal,
      };
    }
    if (action === 'replace' && selectedReplacement && replaceQtyValid) {
      const newTotal = selectedReplacement.price * replaceQtyNum;
      return {
        summary: `${item.product_name} → ${selectedReplacement.name} (${replaceQtyNum} u.)`,
        delta: newTotal - originalLineTotal,
      };
    }
    return null;
  })();

  const handleSubmit = () => {
    if (!item || !action || !canSubmit) return;
    if (action === 'remove') {
      onSubmit({ changeType: 'remove' });
      return;
    }
    if (action === 'reduce_quantity') {
      onSubmit({ changeType: 'reduce_quantity', proposedQuantity: reduceQtyNum });
      return;
    }
    if (action === 'replace' && selectedReplacement) {
      onSubmit({
        changeType: 'replace',
        proposedProductId: selectedReplacement.id,
        proposedQuantity: replaceQtyNum,
      });
    }
  };

  const subtitle = item
    ? `${item.product_name} — ${formatQty(item.quantity, item.unit_of_measure)}`
    : undefined;

  return (
    <ModalShell
      visible={visible}
      onClose={onClose}
      title="Avisar faltante"
      subtitle={subtitle}
      widthVariant="standard"
      keyboardAvoiding
      footer={
        <View style={styles.footerRow}>
          <TouchableOpacity
            style={styles.secondaryBtn}
            onPress={onClose}
            disabled={sending}
            accessibilityRole="button"
            accessibilityLabel="Cancelar"
          >
            <Text style={styles.secondaryBtnText}>Cancelar</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.primaryBtn, !canSubmit && styles.primaryBtnDisabled]}
            onPress={handleSubmit}
            disabled={!canSubmit}
            accessibilityRole="button"
            accessibilityLabel="Mandarle al cliente"
          >
            {sending ? (
              <ActivityIndicator color={ticketColors.goldInk} />
            ) : (
              <Text style={styles.primaryBtnText}>Mandarle al cliente</Text>
            )}
          </TouchableOpacity>
        </View>
      }
    >
      <Text style={styles.sectionLabel}>¿Qué querés proponer?</Text>
      <View style={styles.actionGrid}>
        {ACTION_OPTIONS.map((opt) => {
          const selected = action === opt.key;
          const isDestructive = opt.key === 'remove';
          const disabled = opt.key === 'reduce_quantity' && !canReduce;
          return (
            <TouchableOpacity
              key={opt.key}
              style={[
                styles.actionCard,
                selected && styles.actionCardSelected,
                selected && isDestructive && styles.actionCardSelectedDanger,
                disabled && styles.actionCardDisabled,
              ]}
              disabled={disabled}
              onPress={() => {
                setAction(opt.key);
                if (opt.key !== 'replace') {
                  setSelectedReplacement(null);
                  setReplaceQty('');
                }
                if (opt.key !== 'reduce_quantity') setReduceQty('');
              }}
              accessibilityRole="button"
              accessibilityState={{ selected, disabled }}
            >
              <Text
                style={[
                  styles.actionCardLabel,
                  selected && styles.actionCardLabelSelected,
                  selected && isDestructive && styles.actionCardLabelDanger,
                  disabled && styles.actionCardLabelDisabled,
                ]}
              >
                {opt.label}
              </Text>
              <Text style={styles.actionCardHint}>
                {disabled ? 'Solo hay 1 unidad en el pedido' : opt.hint}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {action === 'replace' ? (
        <View style={styles.configBlock}>
          <Text style={styles.sectionLabel}>Con qué lo reemplazás</Text>
          <TextInput
            style={styles.searchInput}
            placeholder="Buscar producto"
            placeholderTextColor={ticketColors.ink3}
            value={replacementSearch}
            onChangeText={handleSearchChange}
          />
          {replacementLoading ? (
            <ActivityIndicator color={ticketColors.gold} style={{ marginVertical: 12 }} />
          ) : replacementProducts.length === 0 ? (
            <Text style={styles.emptyText}>No hay productos</Text>
          ) : (
            <ScrollView
              style={styles.replacementList}
              nestedScrollEnabled
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              {replacementProducts.map((p) => {
                const isSelected = selectedReplacement?.id === p.id;
                const lowStock = p.stock < (item?.quantity ?? 0);
                return (
                  <TouchableOpacity
                    key={p.id}
                    style={[styles.replacementItem, isSelected && styles.replacementItemSelected]}
                    onPress={() => {
                      setSelectedReplacement(p);
                      setReplaceQty(String(item?.quantity ?? 1));
                    }}
                  >
                    <Text style={styles.replacementItemName}>{p.name}</Text>
                    <Text style={[styles.replacementItemMeta, lowStock && styles.stockWarn]}>
                      {formatMoney(p.price)} · stock: {p.stock}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          )}
          {selectedReplacement ? (
            <View style={styles.qtyBlock}>
              <Text style={styles.qtyLabel}>Cantidad de {selectedReplacement.name}</Text>
              <TextInput
                style={styles.qtyInput}
                placeholder="Cantidad"
                placeholderTextColor={ticketColors.ink3}
                {...qtyInputDefaults}
                value={replaceQty}
                onChangeText={onReplaceQtyChange}
              />
              {replaceQty !== '' && !replaceQtyValid ? (
                <Text style={styles.stockWarn}>Poné un número mayor a 0</Text>
              ) : null}
              {replaceQtyValid && replaceQtyNum > selectedReplacement.stock ? (
                <Text style={styles.stockWarn}>Solo hay {selectedReplacement.stock} en stock</Text>
              ) : null}
            </View>
          ) : null}
        </View>
      ) : null}

      {action === 'reduce_quantity' ? (
        <View style={styles.configBlock}>
          <Text style={styles.sectionLabel}>Nueva cantidad</Text>
          <Text style={styles.hintText}>
            Entre 1 y {maxReduce} (hoy: {formatQty(item?.quantity ?? 0, item?.unit_of_measure)})
          </Text>
          <TextInput
            style={styles.qtyInput}
            placeholder={`1 a ${maxReduce}`}
            placeholderTextColor={ticketColors.ink3}
            {...qtyInputDefaults}
            value={reduceQty}
            onChangeText={onReduceQtyChange}
          />
          {reduceQty !== '' && !reduceQtyValid ? (
            <Text style={styles.stockWarn}>
              Poné un número entre 1 y {maxReduce}
            </Text>
          ) : null}
        </View>
      ) : null}

      {action === 'remove' ? (
        <View style={styles.configBlock}>
          <Text style={styles.removeWarn}>
            Se lo sacamos del pedido si el cliente acepta.
          </Text>
        </View>
      ) : null}

      {preview ? (
        <View style={styles.previewBox}>
          <Text style={styles.previewSummary}>{preview.summary}</Text>
          <Text
            style={[
              styles.previewDelta,
              preview.delta < 0 && styles.previewDeltaDown,
              preview.delta > 0 && styles.previewDeltaUp,
            ]}
          >
            {preview.delta === 0
              ? 'Sin cambio de total'
              : preview.delta < 0
                ? `El total baja ${formatMoney(Math.abs(preview.delta))}`
                : `El total sube ${formatMoney(preview.delta)}`}
          </Text>
        </View>
      ) : null}
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  sectionLabel: {
    color: ticketColors.ink2,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    marginBottom: 8,
    marginTop: 4,
  },
  actionGrid: { gap: 8, marginBottom: 8 },
  actionCard: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 14,
    backgroundColor: ticketColors.paper2,
  },
  actionCardSelected: {
    borderColor: ticketColors.gold,
    borderWidth: 2,
    backgroundColor: ticketColors.paper,
  },
  actionCardSelectedDanger: {
    borderColor: '#FF6B6B',
  },
  actionCardDisabled: {
    opacity: 0.45,
  },
  actionCardLabel: {
    color: ticketColors.ink,
    fontSize: 15,
    fontWeight: '700',
  },
  actionCardLabelSelected: {
    color: ticketColors.gold,
  },
  actionCardLabelDanger: {
    color: '#FF6B6B',
  },
  actionCardLabelDisabled: {
    color: ticketColors.ink3,
  },
  actionCardHint: {
    color: ticketColors.ink3,
    fontSize: 12,
    marginTop: 2,
  },
  configBlock: { marginTop: 8, gap: 8 },
  searchInput: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper,
  },
  emptyText: {
    color: ticketColors.ink3,
    fontSize: 13,
    textAlign: 'center',
    paddingVertical: 12,
  },
  replacementList: {
    maxHeight: 220,
  },
  replacementItem: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    marginBottom: 6,
  },
  replacementItemSelected: {
    borderColor: ticketColors.gold,
    borderWidth: 2,
  },
  replacementItemName: {
    color: ticketColors.ink,
    fontSize: 14,
    fontWeight: '600',
  },
  replacementItemMeta: {
    color: ticketColors.ink3,
    fontSize: 12,
    marginTop: 2,
  },
  stockWarn: { color: '#FF6B6B' },
  qtyBlock: { gap: 6, marginTop: 4 },
  qtyLabel: {
    color: ticketColors.ink,
    fontSize: 13,
    fontWeight: '600',
  },
  qtyInput: {
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper,
  },
  hintText: {
    color: ticketColors.ink3,
    fontSize: 13,
    marginBottom: 4,
  },
  removeWarn: {
    color: ticketColors.ink2,
    fontSize: 14,
    lineHeight: 20,
  },
  previewBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    gap: 4,
  },
  previewSummary: {
    color: ticketColors.ink,
    fontSize: 14,
    fontWeight: '600',
  },
  previewDelta: {
    color: ticketColors.ink3,
    fontSize: 13,
  },
  previewDeltaDown: { color: '#3DD68C' },
  previewDeltaUp: { color: ticketColors.gold },
  footerRow: { flexDirection: 'row', gap: 10 },
  secondaryBtn: {
    flex: 1,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
  },
  secondaryBtnText: {
    color: ticketColors.ink2,
    fontSize: 15,
    fontWeight: '600',
  },
  primaryBtn: {
    flex: 1.4,
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 48,
  },
  primaryBtnDisabled: { opacity: 0.45 },
  primaryBtnText: {
    color: ticketColors.goldInk,
    fontSize: 15,
    fontWeight: '700',
  },
});
